import { useCallback, useEffect, useRef } from "react";

export const useDragAutoScroll = (options = {}) => {
  const { edgeThreshold = 90, maxSpeed = 22, minSpeed = 4 } = options;

  const scrollContainerRef = useRef(null);
  const autoScrollRafRef = useRef(null);
  const autoScrollVelocityRef = useRef(0);

  const stopAutoScroll = useCallback(() => {
    autoScrollVelocityRef.current = 0;
    if (autoScrollRafRef.current !== null) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(() => {
    if (autoScrollRafRef.current !== null) {
      return;
    }

    const step = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        stopAutoScroll();
        return;
      }

      const velocity = autoScrollVelocityRef.current;
      if (!velocity) {
        autoScrollRafRef.current = null;
        return;
      }

      const previousScrollTop = container.scrollTop;
      container.scrollTop += velocity;

      if (container.scrollTop === previousScrollTop) {
        stopAutoScroll();
        return;
      }

      autoScrollRafRef.current = requestAnimationFrame(step);
    };

    autoScrollRafRef.current = requestAnimationFrame(step);
  }, [stopAutoScroll]);

  const handleDragMoveAutoScroll = useCallback(
    (clientY) => {
      const container = scrollContainerRef.current;
      if (!container || typeof clientY !== "number") {
        stopAutoScroll();
        return;
      }

      const rect = container.getBoundingClientRect();
      let velocity = 0;

      if (clientY < rect.top + edgeThreshold) {
        const distance = rect.top + edgeThreshold - clientY;
        const intensity = Math.min(1, distance / edgeThreshold);
        velocity = -Math.max(minSpeed, Math.round(intensity * maxSpeed));
      } else if (clientY > rect.bottom - edgeThreshold) {
        const distance = clientY - (rect.bottom - edgeThreshold);
        const intensity = Math.min(1, distance / edgeThreshold);
        velocity = Math.max(minSpeed, Math.round(intensity * maxSpeed));
      }

      autoScrollVelocityRef.current = velocity;
      if (!velocity) {
        stopAutoScroll();
        return;
      }

      startAutoScroll();
    },
    [edgeThreshold, maxSpeed, minSpeed, startAutoScroll, stopAutoScroll]
  );

  useEffect(() => {
    return () => {
      stopAutoScroll();
    };
  }, [stopAutoScroll]);

  return {
    scrollContainerRef,
    handleDragMoveAutoScroll,
    stopAutoScroll,
  };
};
